// src/services/ProfileService.js
import { db, auth, storage, remoteConfig, fetchAndActivate, getValue } from '../firebase';
import {
  doc,
  updateDoc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
  writeBatch,
  serverTimestamp,
  deleteField,
  GeoPoint,
  Timestamp
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { verifyBeforeUpdateEmail } from 'firebase/auth';
import imageCompression from 'browser-image-compression';

// ✅ Fix: Initialize functions properly
const functions = getFunctions();

// --- 1. CORE DATA FETCHING ---
export const getUserProfile = async (uid) => {
  const docRef = doc(db, 'users', uid);
  const docSnap = await getDoc(docRef);
  return docSnap.exists() ? docSnap.data() : null;
};

// --- 2. UPDATE PROFILE (Name/Photo) ---
export const updateProfileData = async (uid, updates) => {
  const userRef = doc(db, 'users', uid);
  return await updateDoc(userRef, {
    ...updates,
    updatedAt: serverTimestamp()
  });
};

export const uploadProfileImage = async (uid, file) => {
  const options = { maxSizeMB: 0.2, maxWidthOrHeight: 400 };
  const compressedFile = await imageCompression(file, options);
  const storageRef = ref(storage, `user_images/${uid}.jpg`);
  const snapshot = await uploadBytes(storageRef, compressedFile);
  return await getDownloadURL(snapshot.ref);
};

// --- 3. SEARCH & VALIDATION ---
// Needed for merge logic: checks if a phone/email already exists in 'users'
export const findUserByField = async (field, value) => {
  const q = query(collection(db, 'users'), where(field, '==', value));
  const snap = await getDocs(q);
  if (snap.empty) return null;
  return { uid: snap.docs[0].id, ...snap.docs[0].data() };
};

// --- 4. MERGE & SECURITY LOGIC ---
export const callMergeFunction = async (sourceUid) => {
  const mergeFunc = httpsCallable(functions, 'mergeAccounts');
  const result = await mergeFunc({ sourceUid });

  // Refresh the local user token to reflect merged claims
  await auth.currentUser?.reload();
  await auth.currentUser?.getIdToken(true);

  return result.data;
};

export const requestEmailUpdate = async (newEmail) => {
  if (!auth.currentUser) return;
  return await verifyBeforeUpdateEmail(auth.currentUser, newEmail);
};

export const setPendingMerge = async (uid, email, sourceUid) => {
  const userRef = doc(db, 'users', uid);
  return await updateDoc(userRef, {
    pendingEmail: email,
    pendingMergeUid: sourceUid,
    emailChangeRequestedAt: serverTimestamp()
  });
};

// --- 5. THE HEALER (Logic for returning after email verification) ---
export const healPendingEmailUpdates = async () => {
  const user = auth.currentUser;
  if (!user) return { success: false };

  try {
    await user.reload();
    const refreshedUser = auth.currentUser;
    const userRef = doc(db, 'users', refreshedUser.uid);
    const userSnap = await getDoc(userRef);

    if (!userSnap.exists()) return { success: false };

    const data = userSnap.data();
    if (data?.pendingEmail && data.pendingEmail === refreshedUser.email) {
      if (data.pendingMergeUid) {
        const mergeFunc = httpsCallable(functions, 'mergeAccounts');
        await mergeFunc({ sourceUid: data.pendingMergeUid });
      }

      await updateDoc(userRef, {
        email: refreshedUser.email,
        isEmailVerified: true,
        pendingEmail: deleteField(),
        pendingMergeUid: deleteField(),
        emailChangeRequestedAt: deleteField()
      });

      return { success: true, merged: !!data.pendingMergeUid };
    }
  } catch (error) {
    console.error("Healer Error:", error);
    throw error;
  }
  return { success: false };
};

// --- 6. SIGNUP SYNC ---
export const syncUserToFirestoreOld = async (user, signupMethod, currentSessionLocation) => { //Unused, but keeping for reference until we are sure the new one works well
  if (!user) return;
  const userRef = doc(db, 'users', user.uid);
  const userSnap = await getDoc(userRef);

  const expiryDate = new Date();
  expiryDate.setDate(expiryDate.getDate() + 60);

  const locationMap = currentSessionLocation ? {
    district: currentSessionLocation.city || "",
    state: currentSessionLocation.state || "",
    village: currentSessionLocation.village || currentSessionLocation.neighbourhood || "",
    pincode: currentSessionLocation.pincode || "",
    latitude: currentSessionLocation.latitude || 0,
    longitude: currentSessionLocation.longitude || 0,
    geohash: {
      geohash: currentSessionLocation.geoHashData?.geohash || "",
      geopoint: currentSessionLocation.latitude
        ? new GeoPoint(currentSessionLocation.latitude, currentSessionLocation.longitude)
        : null
    }
  } : null;

  if (!userSnap.exists()) {
    const lastFour = user.phoneNumber ? user.phoneNumber.slice(-4) : Math.floor(1000 + Math.random() * 9000);
    const userData = {
      username: user.displayName || `User${lastFour}`,
      email: user.email || "",
      emailVerified: user.emailVerified || false,
      phone_number: user.phoneNumber || "",
      isPhoneVerified: !!user.phoneNumber,
      signupMethod: signupMethod,
      platform: "web",
      profile_completed: false,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      address: locationMap,
      credits: {
        boosts: {
          available: 2,           // What the user can actually spend right now
          totalIssued: 2,         // Lifetime count (great for analytics)
          totalUsed: 0,           // Lifetime count
          expiryDate: Timestamp.fromDate(expiryDate), // Null if promotional boosts don't expire
          lastUsedAt: null        // Timestamp of their last boost execution
        },
        featured: {
          available: 0,           // Future paid features ready to go
          totalIssued: 0,
          totalUsed: 0,
          expiryDate: null,       // Typically null for paid features (no decay)
          lastUsedAt: null
        }
      }
    };
    await updateDoc(userRef, userData); // Use update because doc might be created by trigger
  }
};

export const syncUserToFirestore = async (user, signupMethod, currentSessionLocation) => {
  if (!user) return;
  
  const userRef = doc(db, 'users', user.uid);
  const userSnap = await getDoc(userRef);

  const expiryDate = new Date();
  expiryDate.setDate(expiryDate.getDate() + 60);

  const locationMap = currentSessionLocation ? {
    district: currentSessionLocation.city || "",
    state: currentSessionLocation.state || "",
    village: currentSessionLocation.village || currentSessionLocation.neighbourhood || "",
    pincode: currentSessionLocation.pincode || "",
    latitude: currentSessionLocation.latitude || 0,
    longitude: currentSessionLocation.longitude || 0,
    geohash: {
      geohash: currentSessionLocation.geoHashData?.geohash || "",
      geopoint: currentSessionLocation.latitude
        ? new GeoPoint(currentSessionLocation.latitude, currentSessionLocation.longitude)
        : null
    }
  } : null;

  if (!userSnap.exists()) {
    const lastFour = user.phoneNumber ? user.phoneNumber.slice(-4) : Math.floor(1000 + Math.random() * 9000);
    
    // Check if Founder Mode is active via Remote Config (mirrors mobile app logic)
    let isFounderMember = false;
    try {
      await fetchAndActivate(remoteConfig);
      isFounderMember = getValue(remoteConfig, 'founderMode').asBoolean();
    } catch (e) {
      console.warn('Remote Config fetch failed, defaulting founder_member to false:', e);
    }

    // 1. Initialize the Firestore write batch
    const batch = writeBatch(db);

    // 2. Prepare the main user document data
    const userData = {
      username: user.displayName || `User${lastFour}`,
      email: user.email || "",
      emailVerified: user.emailVerified || false,
      phone_number: user.phoneNumber || "",
      isPhoneVerified: !!user.phoneNumber,
      signupMethod: signupMethod,
      platform: "web",
      profile_completed: false,
      founder_member: isFounderMember,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      address: locationMap,
      
      // Scalable credits structure
      credits: {
        boosts: {
          available: 2,
          totalIssued: 2,
          totalUsed: 0,
          expiryDate: Timestamp.fromDate(expiryDate),
          lastUsedAt: null
        },
        featured: {
          available: 0,
          totalIssued: 0,
          totalUsed: 0,
          expiryDate: null,
          lastUsedAt: null
        }
      }
    };

    // Use "set" with merge: true to safely handle pre-existing shell docs if a backend trigger ran first
    batch.set(userRef, userData, { merge: true });

    // 3. Create the corresponding Ledger Entry in the user's subcollection
    const ledgerRef = doc(collection(userRef, 'ledger')); // Generates a random doc ID inside /users/{uid}/ledger/
    const ledgerData = {
      type: 'charge',
      creditType: 'boosts',
      amount: 2,
      source: 'welcome_offer',
      referenceId: null, // No purchase or ad reference needed for welcome offer
      createdAt: serverTimestamp()
    };

    batch.set(ledgerRef, ledgerData);

    // 4. Commit both operations atomically to the database
    await batch.commit();
  }
};

